import { GetStaticProps } from 'next';
import Head from 'next/head';
import React, { ReactElement } from 'react';

import { ArticleLayout } from '@components/Layouts';
import { TextLink } from '@components/Typography';
import { sanityFetch } from '@lib/sanity.fetch';

interface PageRecord {
  slug: string;
  title: string;
}

export default function SitemapPage({
  slugs,
}: {
  slugs: {
    offences: PageRecord[];
    others: PageRecord[];
    articles: PageRecord[];
  };
}) {
  return (
    <>
      <Head>
        <title>Explore the Monk Lawyers website with our sitemap</title>
        <meta
          name="title"
          content="Explore the Monk Lawyers website with our sitemap"
          key="metatitle"
        />
        <meta
          name="description"
          content="Explore our extensive Monk Lawyers sitemap. Easily navigate to find the article, advice, or contact you need on our site | Monk Lawyers"
          key="metadescription"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/sitemap`}
        />
      </Head>
      <>
        <h1>Monk Lawyers Website Sitemap</h1>
        <h2>Articles</h2>
        <ul>
          {slugs.articles.map((slug) => (
            <li>
              <TextLink href={slug.slug}>{slug.title}</TextLink>
            </li>
          ))}
        </ul>
        <h2>Services</h2>
        <ul>
          {slugs.offences.map((slug) => (
            <li>
              <TextLink href={slug.slug}>{slug.title}</TextLink>
            </li>
          ))}
        </ul>
        <h2>Others</h2>
        <ul>
          {slugs.others.map((slug) => (
            <li>
              <TextLink href={slug.slug}>{slug.title}</TextLink>
            </li>
          ))}
        </ul>
      </>
    </>
  );
}

SitemapPage.getLayout = function getLayout(page: ReactElement) {
  return <ArticleLayout>{page}</ArticleLayout>;
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slugs = await sanityFetch<any>({
    query: `//groq
    {
    "articles": *[_type == "article"
      && !(_id in path("drafts.**")) 
    ]
    {
      "slug": slug.current,
      title
    },
    "offences": *[_type == "offence"
      && !(_id in path("drafts.**")) 
    ]
    {
      "slug": slug.current,
      title
    },
    "others": *[!(_type in ["article","offence"]) 
                && defined(slug) 
                && !(_id in path("drafts.**"))]{
      "slug": slug.current, 
      title
    }
  }
    `,
    draftMode: false,
  });

  // Add static ones, Home, About, Service, Contact
  slugs.others.push({ title: 'Contact', slug: '/contact' });

  return {
    props: {
      slugs,
    },
  };
};
